import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  TextField,
  Select,
  MenuItem,
} from '@mui/material';
import optimoLogo from '../../logo/OptimoCapitalLogoWB.jpg';
import userService from '../../services/api/profile';
import { useAuth } from '../../services/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { SharedContainer } from '../../components/shared-container/container';
import { SharedCard } from '../../components/shared-card/card';
import Navbar from '../../components/shared-menu/Navbar';
import { useTranslation } from 'react-i18next';
import OverlayLoader from '../../components/overlayLoader';

export default function Registration(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { user, setIsAuthenticated, refreshUserData } = useAuth();
  const userRoles = user.role;
  const isConnector = userRoles.includes('connector');
  const userProfile = user.user_profile;
  const phoneVerified = userProfile.phone_number_verified;

  const [isLangaugeSelected, setIsLanguageSelected] = useState(false);
  const [profile, setProfile] = useState({
    phone_number: user.phone_number ?? '',
    email_address: userProfile.email_address ?? '',
    language: userProfile.language ?? '',
    name: [(userProfile.first_name ?? ''), (userProfile.last_name ?? '')].join(' '),
    // user_address_input: userProfile.user_address_input ?? '',
  });
  const [step, setStep] = useState(0);
  const [isEmailValid, setIsEmailValid] = useState(false);

  const navigate = useNavigate();

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'Hindi' },
    { code: 'kn', label: 'Kannada' },
    { code: 'te', label: 'Telugu' },
    { code: 'ta', label: 'Tamil' },
  ];

  const loanTypes = [
    { value: 'personal', label: t('loan_types.personal') },
    { value: 'property_u_25l', label: t('loan_types.property_u_25l') },
    { value: 'property_b_25l_50l', label: t('loan_types.property_b_25l_50l') },
    { value: 'property_o_50l', label: t('loan_types.property_a_50l') },
    { value: 'unsecured_business', label: t('loan_types.unsecured_business') },
    { value: 'home', label: t('loan_types.home') },
  ];

  const [errors, setErrors] = useState({});

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));

    if (name === 'email_address') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsEmailValid(emailRegex.test(value));
    }
    if (name === 'language') {
      setIsLanguageSelected(!!value);
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setProfile((prevProfile) => {
      const currentLoanTypes = prevProfile.loan_type || [];
      return {
        ...prevProfile,
        loan_type: checked ? [...currentLoanTypes, value] : currentLoanTypes.filter((type) => type !== value),
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const validationErrors = {};
    let isValid = true;

    if (!isConnector && (!profile.email_address || !isEmailValid)) {
      validationErrors.email_address = t('errors.invalid_email');
      isValid = false;
    }

    if (!userRoles.includes('borrower')) {
      if (!profile.loan_type || profile.loan_type.length === 0) {
        validationErrors.loan_type = t('loan_types.errors.select');
        isValid = false;
      }
      if (!profile.pincode || !/^\d{6}$/.test(profile.pincode)) {
        validationErrors.pincode = t('pincode.errors.invalid');
        isValid = false;
      }
      if (!profile.is_loan_agent) {
        validationErrors.is_loan_agent = t('registration.errors.is_loan_agent');
        isValid = false;
      }
      if (profile.is_loan_agent === 'yes' && !profile.loan_case_count) {
        validationErrors.loan_case_count = t('registration.errors.loan_case_count');
        isValid = false;
      }
    }

    if (!profile.name.trim()) {
      validationErrors.name = t('errors.name_required');
      isValid = false;
    }
    // if (!profile.user_address_input) {
    //   validationErrors.user_address_input = t('errors.address_required');
    //   isValid = false;
    // }

    setErrors(validationErrors);

    if (isValid) {
      setLoading(true);
      const [firstName, ...lastNameParts] = profile.name.split(' ').filter(Boolean);
      const payload = {
        ...profile,
        first_name: firstName,
        last_name: lastNameParts.join(' ') || null,
      };
      userService.registerUser(payload, setIsAuthenticated, navigate)
        .then(() => {
          // Reload the user so its onboarding_status (now awaiting_kyc) reaches
          // ProtectedRoute, which then moves to /kyc. Navigating straight to /kyc
          // with the stale 'initialised' status bounced back to a fresh step 1.
          refreshUserData();
        })
        .catch(() => {
          setLoading(false);
          alert(t('errors.something_went_wrong'))
        });
    }
  };

  const handleNext = () => setStep((prevStep) => prevStep + 1);
  const handleBack = () => setStep((prevStep) => prevStep - 1);

  return (
      <SharedContainer direction="column" justifyContent="space-between">
        {loading && <OverlayLoader open={loading} text="" />}
        <Navbar isTransparent />
        <SharedCard>
          <img src={optimoLogo} alt="Optimo Logo" style={{ width: '80px', objectFit: 'contain' }} />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h4" component="h1">
              {t('registration.header')} {step + 1}
            </Typography>
            <Button variant="outlined" onClick={handleBack} disabled={step === 0}>
              <ArrowBackIosNewIcon />
            </Button>
          </Box>
          {step === 0 && (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl>
                <FormLabel>{t('phone_number.label')}</FormLabel>
                <TextField fullWidth id="phone_number" name="phone_number" value={profile.phone_number} disabled />
                <Typography variant="body2" color={phoneVerified ? 'success.main' : 'error.main'}>
                  {phoneVerified ? t('phone_number.verified') : t('phone_number.not_verified')}
                </Typography>
              </FormControl>
              <FormControl>
                <FormLabel>{t('email_address.label')}</FormLabel>
                <TextField
                  fullWidth
                  id="email_address"
                  name="email_address"
                  value={profile.email_address}
                  onChange={handleInputChange}
                />
                {errors.email_address && <Typography color="error">{errors.email_address}</Typography>}
              </FormControl>
              {
                isConnector ?
                <Button fullWidth variant="contained" onClick={handleNext}>
                  {t('next')}
                </Button>
                :
                <Button fullWidth variant="contained" onClick={handleNext} disabled={!isEmailValid }>
                  {t('next')}
                </Button>
              }
            </Box>
          )}
          {step === 1 && (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl>
                <FormLabel>{t('registration.select_primary_language')}</FormLabel>
                <Select
                  id="language"
                  name="language"
                  value={profile.language}
                  onChange={handleInputChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t('registration.select_primary_language')}
                  </MenuItem>
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button fullWidth variant="contained" onClick={handleNext} disabled={!profile.language}>
                {t('next')}
              </Button>
            </Box>
          )}
          {step === 2 && (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onSubmit={handleSubmit}>
              {!userRoles.includes('borrower') && (
                <>
                  <FormControl>
                    <FormLabel>{t('registration.familiar_loan_type.label')}</FormLabel>
                    <FormGroup>
                      {loanTypes.map((loanType) => (
                        <FormControlLabel
                          key={loanType.value}
                          control={
                            <Checkbox
                              checked={profile.loan_type?.includes(loanType.value) || false}
                              onChange={handleCheckboxChange}
                              value={loanType.value}
                            />
                          }
                          label={loanType.label}
                        />
                      ))}
                    </FormGroup>
                    {errors.loan_type && <Typography color="error">{errors.loan_type}</Typography>}
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('registration.pincode_input_label')}</FormLabel>
                    <TextField
                      fullWidth
                      id="pincode"
                      name="pincode"
                      value={profile.pincode || ''}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    />
                    {errors.pincode && <Typography color="error">{errors.pincode}</Typography>}
                  </FormControl>
                </>
              )}
              <FormControl>
                <FormLabel>{t('registration.aadhaar_name_input_label')}</FormLabel>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  value={profile.name || ''}
                  onChange={handleInputChange}
                />
                {errors.name && <Typography color="error">{errors.name}</Typography>}
              </FormControl>
              {/* <FormControl>
                <FormLabel>Address as per KYC</FormLabel>
                <TextField
                  fullWidth
                  id="user_address_input"
                  name="user_address_input"
                  value={profile.user_address_input || ''}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                />
                {errors.user_address_input && <Typography color="error">{errors.user_address_input}</Typography>}
              </FormControl> */}
              {!userRoles.includes('borrower') && (
                <>
                  <FormControl>
                    <FormLabel>{t('registration.current_years_work_exp.label')}</FormLabel>
                    <Select
                      fullWidth
                      id="work_experience"
                      name="work_experience"
                      value={profile.work_experience || ''}
                      onChange={handleInputChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {t('registration.current_years_work_exp.placeholder')}
                      </MenuItem>
                      <MenuItem value="0_1">
                        {t('registration.current_years_work_exp.options.0_1')}
                      </MenuItem>
                      <MenuItem value="1_5">
                        {t('registration.current_years_work_exp.options.1_5')}
                      </MenuItem>
                      <MenuItem value="5_10">
                        {t('registration.current_years_work_exp.options.5_10')}
                      </MenuItem>
                      <MenuItem value="10_plus">
                        {t('registration.current_years_work_exp.options.10_plus')}
                      </MenuItem>
                    </Select>
                    {errors.work_experience && (
                      <Typography color="error">{errors.work_experience}</Typography>
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('registration.existing_loan_agent.label')}</FormLabel>
                    <Select
                      fullWidth
                      id="is_loan_agent"
                      name="is_loan_agent"
                      value={profile.is_loan_agent || ''}
                      onChange={handleInputChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {t('choose')}
                      </MenuItem>
                      <MenuItem value="yes">{t('yes')}</MenuItem>
                      <MenuItem value="no">{t('no')}</MenuItem>
                    </Select>
                    {errors.is_loan_agent && (
                      <Typography color="error">{errors.is_loan_agent}</Typography>
                    )}
                  </FormControl>
                  {profile.is_loan_agent === 'yes' && (
                    <FormControl>
                      <FormLabel>{t('registration.existing_loan_agent.loan_case_count_input_label')}</FormLabel>
                      <TextField
                        fullWidth
                        id="loan_case_count"
                        name="loan_case_count"
                        value={profile.loan_case_count || ''}
                        onChange={handleInputChange}
                        type="number"
                        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                      />
                      {errors.loan_case_count && (
                        <Typography color="error">{errors.loan_case_count}</Typography>
                      )}
                    </FormControl>
                  )}
                </>
              )}
              <Button 
                type="button" 
                fullWidth 
                variant="contained" 
                onClick={handleSubmit} 
              >
                {t('submit')}
              </Button>
            </Box>
          )}
        </SharedCard>
      </SharedContainer>
  );
}
